/**
 * ===== PROFESSIONAL LOGGER - SYST\u00c8ME DE LOGGING =====
 * Remplace tous les console.log par un syst\u00e8me de logging professionnel;
 */
import { isProd } from './envHelper';

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  FATAL = 4,
}
export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: unknown;
  source?: string;
  userId?: string;
  sessionId?: string;
}

export interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  enableStorage: boolean;
  enableRemote: boolean;
  maxStorageEntries: number;
  remoteEndpoint?: string;
  userId?: string;
  sessionId?: string;
}
export class ProfessionalLogger {
  private static instance: ProfessionalLogger;
  private config: LoggerConfig;
  private logs: LogEntry[] = [];
  private isInitialized: boolean = false;
  private constructor() {
    // PHASE 4.2: Détecter le mode production et désactiver la console
    // Support pour Jest (process.env) et Vite (import.meta.env)
    const isProduction = isProd();
    this.config = {
      level: isProduction ? LogLevel.WARN : LogLevel.INFO, // En production, seulement WARN et ERROR
      enableConsole: !isProduction, // Désactiver la console en production
      enableStorage: true,
      enableRemote: false,
      maxStorageEntries: 1000,
      userId: this.generateUserId(),
      sessionId: this.generateSessionId(),
    };
  }
  public static getInstance(): ProfessionalLogger {
    if (!ProfessionalLogger.instance) {
      ProfessionalLogger.instance = new ProfessionalLogger();
    }
    return ProfessionalLogger.instance;
  }
  /**
   * Initialise le logger;
   */
  public initialize(config: Partial<LoggerConfig> = {}): void {
    this.config = { ...this.config, ...config };
    this.isInitialized = true;
    this.info('Logger initialis\u00e9', { config: this.config });
  }
  /**
   * Log de debug;
   */
  public debug(message: string, context?: unknown, source?: string): void {
    this.log(LogLevel.DEBUG, message, context, source);
  }
  /**
   * Log d'information;
   */
  public info(message: string, context?: unknown, source?: string): void {
    this.log(LogLevel.INFO, message, context, source);
  }
  /**
   * Log d'avertissement;
   */
  public warn(message: string, context?: unknown, source?: string): void {
    this.log(LogLevel.WARN, message, context, source);
  }
  /**
   * Log d'erreur;
   */
  public error(message: string, context?: unknown, source?: string): void {
    this.log(LogLevel.ERROR, message, context, source);
  }
  /**
   * Log fatal;
   */
  public fatal(message: string, context?: unknown, source?: string): void {
    this.log(LogLevel.FATAL, message, context, source);
  }
  /**
   * Log principal;
   */
  private log(level: LogLevel, message: string, context?: unknown, source?: string): void {
    if (!this.isInitialized) {
      this.initialize();
    }
    if (level < this.config.level) {
      return;
    }
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context,
      source,
      userId: this.config.userId,
      sessionId: this.config.sessionId,
    };
    // Ajouter au stockage;
    if (this.config.enableStorage) {
      this.addToStorage(entry);
    }
    // Afficher dans la console;
    if (this.config.enableConsole) {
      this.logToConsole(entry);
    }
    // Envoyer au serveur;
    if (this.config.enableRemote && this.config.remoteEndpoint) {
      this.sendToRemote(entry);
    }
  }
  /**
   * Ajoute une entr\u00e9e au stockage;
   */
  private addToStorage(entry: LogEntry): void {
    this.logs.push(entry);
    // Limiter le nombre d'entrées;
    if (this.logs.length > this.config.maxStorageEntries) {
      this.logs = this.logs.slice(-this.config.maxStorageEntries);
    }
    // Sauvegarder dans localStorage;
    try {
      localStorage.setItem('snapbooth_logs', JSON.stringify(this.logs.slice(-100)));
    } catch (_error) {
      // Ignorer les erreurs de localStorage;
    }
  }

  /**
   * Affiche dans la console;
   */
  private logToConsole(entry: LogEntry): void {
    const timestamp = new Date(entry.timestamp).toLocaleTimeString();
    const levelName = LogLevel[entry.level];
    const source = entry.source ? `[${entry.source}]` : '';
    const context = entry.context ? JSON.stringify(entry.context, null, 2) : '';
    const logMessage = `[${timestamp}] ${levelName} ${source}: ${entry.message}`;
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(logMessage, context);
        break;
      case LogLevel.INFO:
        console.info(logMessage, context);
        break;
      case LogLevel.WARN:
        console.warn(logMessage, context);
        break;
      case LogLevel.ERROR:
        console.error(logMessage, context);
        break;
      case LogLevel.FATAL:
        console.error(`🚨 FATAL: ${logMessage}`, context);
        break;
    }
  }
  /**
   * Envoie au serveur distant;
   */
  private async sendToRemote(entry: LogEntry): Promise<void> {
    try {
      if (!this.config.remoteEndpoint) {
        return;
      }
      await fetch(this.config.remoteEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(entry),
      });
    } catch (_error) {
      // Ignorer les erreurs de réseau;
    }
  }
  /**
   * Obtient les logs;
   */
  public getLogs(level?: LogLevel, limit?: number): LogEntry[] {
    let filteredLogs = this.logs;
    if (level !== undefined) {
      filteredLogs = filteredLogs.filter(log => log.level >= level);
    }
    if (limit) {
      filteredLogs = filteredLogs.slice(-limit);
    }
    return filteredLogs;
  }
  /**
   * Obtient les statistiques des logs;
   */
  public getStatistics(): {
    totalLogs: number;
    debugCount: number;
    infoCount: number;
    warnCount: number;
    errorCount: number;
    fatalCount: number;
    oldestLog: string | null;
    newestLog: string | null;
  } {
    const stats = {
      totalLogs: this.logs.length,
      debugCount: 0,
      infoCount: 0,
      warnCount: 0,
      errorCount: 0,
      fatalCount: 0,
      oldestLog: null as string | null,
      newestLog: null as string | null,
    };
    if (this.logs.length === 0) {
      return stats;
    }
    this.logs.forEach(log => {
      switch (log.level) {
        case LogLevel.DEBUG:
          stats.debugCount++;
          break;
        case LogLevel.INFO:
          stats.infoCount++;
          break;
        case LogLevel.WARN:
          stats.warnCount++;
          break;
        case LogLevel.ERROR:
          stats.errorCount++;
          break;
        case LogLevel.FATAL:
          stats.fatalCount++;
          break;
      }
    });
    stats.oldestLog = this.logs[0].timestamp;
    stats.newestLog = this.logs[this.logs.length - 1].timestamp;
    return stats;
  }
  /**
   * Exporte les logs;
   */
  public exportLogs(format: 'json' | 'csv' = 'json'): string {
    if (format === 'csv') {
      const headers = 'timestamp,level,message,source,userId,sessionId\n';
      const rows = this.logs
        .map(
          log =>
            `"${log.timestamp}","${LogLevel[log.level]}","${log.message}","${log.source || ''}","${log.userId || ''}","${log.sessionId || ''}"`
        )
        .join('\n');
      return headers + rows;
    }
    return JSON.stringify(this.logs, null, 2);
  }
  /**
   * Vide les logs;
   */
  public clearLogs(): void {
    this.logs = [];
    try {
      localStorage.removeItem('snapbooth_logs');
    } catch (_error) {
      // Ignorer les erreurs de localStorage;
    }
  }

  /**
   * Charge les logs depuis localStorage;
   */
  public loadLogsFromStorage(): void {
    try {
      const stored = localStorage.getItem('snapbooth_logs');
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (_error) {
      // Ignorer les erreurs de localStorage;
    }
  }
  /**
   * G\u00e9n\u00e8re un ID utilisateur;
   */
  private generateUserId(): string {
    return `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  /**
   * G\u00e9n\u00e8re un ID de session;
   */
  private generateSessionId(): string {
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  /**
   * Nettoie les ressources;
   */
  public destroy(): void {
    this.clearLogs();
    this.isInitialized = false;
  }
}
// Instance globale;
export const logger = ProfessionalLogger.getInstance();
